import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, useT } from '@/i18n';
import { Alert, Button } from '@/components/ui';
import { splitTitle } from './components';
import type { ProgrammeState } from './useProgramme';

const shortDate = (iso: string, locale: string) =>
  new Date(iso).toLocaleDateString(locale === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'short' });

/**
 * The activity's authored goal, and "we reached it": the parent decides, and the next activity
 * in the same skill chain takes this one's place in the programme.
 */
export const GoalCard = ({ exerciseId, assessmentId, state }: { exerciseId: string; assessmentId: string; state: ProgrammeState }) => {
  const t = useT();
  const { locale } = useLocale();
  const [confirming, setConfirming] = useState(false);
  const progression = state.data?.progression;
  const goal = progression?.goals[exerciseId];
  if (!progression || !goal) return null;

  const reached = progression.reached.find((g) => g.exerciseId === exerciseId);
  const cameFrom = progression.reached.find((g) => g.nextExerciseId === exerciseId);
  const titleOf = (id?: string) => (id ? splitTitle(state.blocks.get(id)?.title ?? '')[0] : undefined);
  const next = reached?.nextExerciseId;

  return (
    <section aria-labelledby="goal" className="rounded-lg border-[1.5px] border-sage-300 bg-white p-5">
      {cameFrom && titleOf(cameFrom.exerciseId) && (
        <p className="mb-2 text-caption font-extrabold uppercase tracking-wide text-lagoon-600">
          {t('goal.nextAfter', { title: titleOf(cameFrom.exerciseId)! })}
        </p>
      )}
      <h2 id="goal" className="text-[18px]">{t('goal.title')}</h2>
      <p className="mt-1 text-body text-ink-soft">{goal}</p>

      {reached ? (
        <div className="mt-4 flex flex-col gap-2">
          <p className="text-body font-extrabold text-[#2F5B40]">{t('goal.reachedOn', { date: shortDate(reached.reachedAt, locale) })}</p>
          {next ? (
            <Link
              to={`/programme/${assessmentId}/${next}`}
              className="inline-flex min-h-tap items-center text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline"
            >
              {t('goal.seeNext', { title: titleOf(next) ?? '' })} →
            </Link>
          ) : (
            <p className="text-body text-ink-muted">{t('goal.endOfChain')}</p>
          )}
          {progression.undoable.includes(exerciseId) && (
            <Button variant="tertiary" className="self-start" loading={state.saving} onClick={() => void state.undoGoal(exerciseId)}>
              {t('goal.undo')}
            </Button>
          )}
        </div>
      ) : confirming ? (
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-body text-ink-soft">{t('goal.confirmBody')}</p>
          <div className="flex flex-wrap gap-3">
            <Button
              loading={state.saving}
              onClick={async () => {
                const res = await state.reachGoal(exerciseId);
                if (res.ok) setConfirming(false);
              }}
            >
              {t('goal.confirm')}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t('goal.notYet')}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" className="mt-4" onClick={() => setConfirming(true)}>
          {t('goal.reached')}
        </Button>
      )}
      {state.saveError && confirming && (
        <Alert tone="warn" className="mt-3">
          {t('act.saveFailed')}
        </Alert>
      )}
    </section>
  );
};
